import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Inbox, Newspaper, FileText, TrendingUp } from "lucide-react";
import { adminApi } from "../../lib/adminApi";

const TYPE_LABEL = { quick: "Quick", quote: "Quote", contact: "Contact" };
const STATUS_BADGE = {
  new: "bg-ridge-amber/15 text-ridge-amber-dark",
  contacted: "bg-blue-100 text-blue-800",
  won: "bg-green-100 text-green-800",
  lost: "bg-zinc-200 text-zinc-700",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await adminApi.leadStats();
      if (res.ok) setStats(res.data);
      setLoading(false);
    })();
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <header className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <span className="overline">Overview</span>
          <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-ridge-charcoal md:text-4xl">
            Dashboard
          </h1>
          <p className="mt-2 text-sm text-ridge-muted">
            Lead activity and content status at a glance.
          </p>
        </div>
        <Link to="/admin/leads" className="btn-primary" data-testid="admin-cta-leads">
          Open lead inbox <ArrowRight className="h-4 w-4" />
        </Link>
      </header>

      {loading ? (
        <p className="mt-8 text-sm text-ridge-muted">Loading…</p>
      ) : !stats ? (
        <p className="mt-8 text-sm text-red-600">Could not load stats.</p>
      ) : (
        <>
          <section className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
            <StatCard label="Total Leads" value={stats.total} icon={Inbox} />
            <StatCard label="Last 30 Days" value={stats.last_30d} icon={TrendingUp} />
            <StatCard label="Last 7 Days" value={stats.last_7d} icon={TrendingUp} />
            <StatCard label="Open (New)" value={stats.by_status?.new || 0} icon={Inbox} />
          </section>

          <section className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            <BreakdownCard
              title="By Type"
              entries={Object.entries(stats.by_type || {}).map(([k, v]) => [TYPE_LABEL[k] || k, v])}
            />
            <BreakdownCard
              title="By Status"
              entries={Object.entries(stats.by_status || {}).map(([k, v]) => [k, v])}
            />
          </section>

          <section className="mt-10">
            <div className="flex items-baseline justify-between">
              <h2 className="font-display text-lg font-semibold tracking-tight text-ridge-charcoal md:text-xl">
                Recent leads
              </h2>
              <Link
                to="/admin/leads"
                className="text-xs font-semibold uppercase tracking-[0.16em] text-ridge-amber hover:text-ridge-amber-dark"
              >
                View all
              </Link>
            </div>
            <div className="mt-4 overflow-hidden rounded-md border border-ridge-border bg-white">
              <table className="w-full text-sm" data-testid="admin-recent-leads">
                <thead className="bg-ridge-stone/60 text-xs uppercase tracking-[0.14em] text-ridge-muted">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Date</th>
                    <th className="px-4 py-3 text-left font-semibold">Type</th>
                    <th className="px-4 py-3 text-left font-semibold">Name</th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-ridge-border">
                  {(stats.recent || []).map((lead) => (
                    <tr key={lead.id} className="hover:bg-ridge-stone/40">
                      <td className="whitespace-nowrap px-4 py-3 text-ridge-muted">
                        {(lead.created_at || "").slice(0, 10)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-md bg-ridge-stone px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-ridge-charcoal">
                          {TYPE_LABEL[lead.type] || lead.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-ridge-charcoal">{lead.name}</td>
                      <td className="px-4 py-3 text-ridge-text">{lead.email}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-md px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-[0.12em] ${STATUS_BADGE[lead.status] || ""}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/leads?selected=${lead.id}`}
                          className="text-xs font-semibold uppercase tracking-[0.14em] text-ridge-amber hover:text-ridge-amber-dark"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {(!stats.recent || stats.recent.length === 0) ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-sm text-ridge-muted">
                        No leads yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            <ContentCard label="Published Blog Posts" value={stats.blog_published || 0} to="/admin/blog" icon={Newspaper} />
            <ContentCard label="Published Case Studies" value={stats.case_studies_published || 0} to="/admin/case-studies" icon={FileText} />
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-md border border-ridge-border bg-white p-5 md:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-ridge-muted">{label}</span>
        <Icon className="h-4 w-4 text-ridge-amber" />
      </div>
      <p className="font-display mt-3 text-3xl font-bold tracking-tight text-ridge-charcoal md:text-4xl">{value}</p>
    </div>
  );
}

function BreakdownCard({ title, entries }) {
  const total = entries.reduce((acc, [, v]) => acc + Number(v || 0), 0) || 1;
  return (
    <div className="rounded-md border border-ridge-border bg-white p-6">
      <h3 className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-ridge-charcoal">{title}</h3>
      <ul className="mt-4 space-y-3">
        {entries.map(([label, value]) => {
          const pct = Math.round((Number(value || 0) / total) * 100);
          return (
            <li key={label}>
              <div className="flex justify-between text-sm">
                <span className="font-medium capitalize text-ridge-text">{label}</span>
                <span className="font-semibold text-ridge-charcoal">{value}</span>
              </div>
              <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-ridge-stone">
                <div className="h-full bg-ridge-amber" style={{ width: `${pct}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ContentCard({ label, value, to, icon: Icon }) {
  return (
    <Link
      to={to}
      className="group flex items-center justify-between rounded-md border border-ridge-border bg-white p-6 transition-colors hover:border-ridge-charcoal/25"
    >
      <div>
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-ridge-muted">{label}</span>
        <p className="font-display mt-2 text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">{value}</p>
      </div>
      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-ridge-stone text-ridge-amber group-hover:bg-ridge-amber group-hover:text-white">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
    </Link>
  );
}
